import React, { useEffect, useRef, useState } from 'react';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import _ from 'lodash';
import { FaMapMarkerAlt } from 'react-icons/fa';
import { MdClose } from 'react-icons/md';

const libraries: any = ['places'];

const GoogleSearchMap = ({ setLocation, location, placeholder, className }: any) => {
  const [inputValue, setInputValue] = useState(location?.description || '');
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [mapCenter, setMapCenter] = useState({ lat: 11.059821, lng: 78.387451 }); // default: Tamil Nadu
  const autocompleteServiceRef = useRef<any>(null);
  const geocoderRef = useRef<any>(null);
  const [isOpen, setIsOpen] = useState(false);
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: 'AIzaSyDOCJ7V-QaS9nnf7zZ1B7ZEgrkI-YcxWA0', // replace with your key
    libraries,
  });

  useEffect(() => {
    if (isLoaded && !autocompleteServiceRef.current) {
      autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
      geocoderRef.current = new window.google.maps.Geocoder();
    }
  }, [isLoaded]);

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);

    if (!value) return setSuggestions([]);

    if (autocompleteServiceRef.current) {
      autocompleteServiceRef.current.getPlacePredictions(
        { input: value },
        (predictions: any[], status: string) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            setSuggestions(predictions);
          }
        }
      );
    }
  };

  const handleSelectSuggestion = (place: any) => {
    setInputValue(place.description);
    setSuggestions([]);

    const placesService = new window.google.maps.places.PlacesService(document.createElement('div'));

    placesService.getDetails({ placeId: place.place_id }, (details: any, status: string) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK && details.geometry) {
        const pincodeData: any = _.last(details.address_components)
        const lat = details.geometry.location.lat();
        const lng = details.geometry.location.lng();
        setLocation({
          lat,
          lng,
          name: details.name,
          description: details.formatted_address,
          pincode: pincodeData?.long_name,
        });
        setMapCenter({ lat, lng });
      }
    });
  };

  const handleMarkerDragEnd = (e: google.maps.MapMouseEvent) => {
    if (!e.latLng || !geocoderRef.current) return;
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();

    geocoderRef.current.geocode({ location: { lat, lng } }, (results: any, status: string) => {
      if (status === 'OK' && results[0]) {
        const pincodeData: any = _.last(results[0].address_components)

        setLocation({
          lat,
          lng,
          name: results[0].address_components?.[0]?.long_name || '',
          description: results[0].formatted_address,
          pincode: pincodeData?.long_name,
        });
        setInputValue(results[0].formatted_address);
      }
    });

    setMapCenter({ lat, lng });
  };

  const handleMapClick = (e: google.maps.MapMouseEvent) => {
    if (!e.latLng || !geocoderRef.current) return;
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();

    geocoderRef.current.geocode({ location: { lat, lng } }, (results: any, status: string) => {
      if (status === 'OK' && results[0]) {
        const pincodeData: any = _.last(results[0].address_components)

        setLocation({
          lat,
          lng,
          name: results[0].address_components?.[0]?.long_name || '',
          description: results[0].formatted_address,
          pincode: pincodeData?.long_name,
        });
        setInputValue(results[0].formatted_address);
      }
    });

    setMapCenter({ lat, lng });
  };

  const clearSearch = () => {
    setInputValue('');
    setSuggestions([]);
    setLocation(null);
  };

  if (!isLoaded) return <div>Loading...</div>;
  const handleOpenMap = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="w-full">
      <div className="relative">
        <div onClick={handleOpenMap} title="Click to select location on the map" className="flex absolute inset-y-0 left-0 items-center pl-2 cursor-pointer">
          <FaMapMarkerAlt className="skin-base opacity-60 text-black" />
        </div>
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          placeholder={placeholder || 'Search or tap map to mark location'}
          className={className ? className : 'w-full border-b-2 border-black ps-8  pe-10 py-1'}
        />
        {inputValue && (
          <button
            onClick={clearSearch}
            className="outline-none absolute top-0 end-0 w-14 md:w-16 h-full flex items-center justify-center transition duration-200 ease-in-out hover:text-heading focus:outline-none"
          >
            <MdClose className="w-[17px] h-[17px] text-skin-base text-black text-opacity-40" />
          </button>
        )}
        {suggestions.length > 0 && (
          <ul className="absolute z-10 bg-white shadow-lg w-full mt-1 rounded max-h-60 overflow-y-auto">
            {suggestions.map((suggestion) => (
              <li
                key={suggestion.place_id}
                className="px-4 py-2 text-black font-normal hover:bg-gray-100 cursor-pointer"
                onClick={() => handleSelectSuggestion(suggestion)}
              >
                {suggestion.description}
              </li>
            ))}
          </ul>
        )}
      </div>
      {isOpen &&
        <div className="w-full h-[300px] mt-4">
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={mapCenter}
            zoom={location ? 15 : 5}
            onClick={handleMapClick}
          >

            <Marker
              position={{ lat: location?.lat || 11.059821, lng: location?.lng || 78.387451 }}
              draggable={true}
              onDragEnd={handleMarkerDragEnd}
            />

          </GoogleMap>
        </div>}
    </div>
  );
};

export default GoogleSearchMap;
